import DescriptionDetails from "components/DescriptionDetails";
import FormFieldViewText from "components/FormFields/ViewText";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import { Blueprint, BlueprintInput } from "types/generated";
import useAnnounce from "hooks/useAnnounce";
import useCanManageBlueprint from "shared/Blueprint/access/useCanManageBlueprint";

import TemplatePreviewSkeleton from "./Skeleton";
import BlueprintTemplateFormField from "./FormField";
import styles from "./styles.module.css";

type BlueprintTemplateFormProps = {
  item: Blueprint;
  isPublished: boolean;
  hasErrors: boolean;
  hasPreview: boolean;
  loading?: boolean;
  errors: string[];
  inputs: BlueprintInput[];
  /*
   * This is a callback to edit the description field, when not passed - the edit button will not be visible
   */
  handleEditDescription?: () => void;
  onOpenFullDescription: () => void;
};

const BlueprintTemplateForm = ({
  item,
  isPublished,
  hasPreview,
  hasErrors,
  errors,
  inputs,
  loading,
  handleEditDescription,
  onOpenFullDescription,
}: BlueprintTemplateFormProps) => {
  const canManageBlueprint = useCanManageBlueprint()(item);
  const showErrors = !isPublished && hasErrors;

  useAnnounce(showErrors ? errors.join(". ") : undefined);

  return (
    <Box direction="column" fullWidth gap="large">
      {isPublished && (
        <>
          <FormFieldViewText label="Blueprint name" value={item.name} noMargin />

          <DescriptionDetails
            noMargin
            label="Blueprint description"
            description={item.description}
            onOpenFullDescription={onOpenFullDescription}
            {...(canManageBlueprint.result &&
              handleEditDescription && { onAddDescription: handleEditDescription })}
          />
        </>
      )}

      {!isPublished && !hasPreview && !hasErrors && (
        <Banner variant="info">
          No preview available. Please add some inputs to the template.
        </Banner>
      )}

      {!isPublished && hasPreview && !hasErrors && (
        <Banner variant="info">
          This is just a template preview, you will not be able to create a stack from it.
        </Banner>
      )}

      {showErrors &&
        errors.map((error, index) => (
          <Banner className={styles.errorBanner} variant="danger" key={index}>
            {error}
          </Banner>
        ))}

      {loading && <TemplatePreviewSkeleton />}
      {!loading && !hasErrors && (
        <Box direction="column" grow="1" fullWidth gap="large">
          {inputs.map((input) => (
            <BlueprintTemplateFormField key={input.id} input={input} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default BlueprintTemplateForm;
